import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import StarIcon from "@mui/icons-material/Star";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import CatalogInteresesPanel from "./CatalogInteresesPanel";
import PhoneWithCountryField from "../shared/PhoneWithCountryField";
import {
  formatPhoneDisplay,
  isValidFullPhone,
  normPhoneDigits,
  normalizeFullPhone,
  whatsappHref,
} from "../../utils/phoneCountryCodes";

const initialForm = {
  nombre: "",
  whatsapp: "",
  ciudad: "",
  zona: "",
  estilo_preferido: "CASUAL",
  sensibilidad_precio: "MEDIA",
  talla_tops: "",
  talla_jeans: "",
  talla_vestidos: "",
  talla_calzado: "",
  colores_preferidos: "",
};

const whatsappFlows = [
  {
    key: "POST_COMPRA",
    label: "Post-compra",
    plantilla: "POST_COMPRA_RECOMENDACION",
  },
  {
    key: "NUEVA_COLECCION",
    label: "Nueva colección",
    plantilla: "NUEVA_COLECCION_AFINIDAD",
  },
  {
    key: "REPOSICION_TALLA",
    label: "Reposición talla",
    plantilla: "REPOSICION_TALLA_FAVORITA",
  },
  {
    key: "RECORDATORIO_INTELIGENTE",
    label: "Recordatorio inteligente",
    plantilla: "REACTIVACION_NO_SPAM",
  },
];

const segmentoColor = {
  VIP: "warning",
  FRECUENTE: "success",
  OCASIONAL: "primary",
  NUEVO: "default",
};

const riesgoColor = {
  ALTO: "error",
  MEDIO: "warning",
  BAJO: "success",
};

export default function ClientesCRM({ store }) {
  const [form, setForm] = useState(initialForm);
  const [feedback, setFeedback] = useState(null);
  const [waSnack, setWaSnack] = useState(null);
  const [waInline, setWaInline] = useState(null);
  const [segmentoFiltro, setSegmentoFiltro] = useState("TODOS");
  const [riesgoFiltro, setRiesgoFiltro] = useState("TODOS");

  const notifyWhatsappFlow = (severity, msg, { clienteId, flowLabel } = {}) => {
    setWaSnack({ severity, msg });
    if (severity === "success") {
      setWaInline((prev) => (prev?.clienteId === clienteId ? null : prev));
      return;
    }
    if (clienteId) {
      setWaInline({ clienteId, msg, flowLabel: flowLabel || "" });
    }
  };

  const clientes = store.clientesSegmentados || [];

  const resumen = useMemo(() => {
    const total = clientes.length;
    const vip = clientes.filter((c) => c.stats.segmento === "VIP").length;
    const riesgoAlto = clientes.filter(
      (c) => c.stats.riesgoChurn === "ALTO"
    ).length;
    const activosWA = clientes.filter((c) => c.consentimiento_whatsapp).length;
    return { total, vip, riesgoAlto, activosWA };
  }, [clientes]);

  const clientesFiltrados = useMemo(() => {
    return clientes.filter((c) => {
      if (segmentoFiltro !== "TODOS" && c.stats.segmento !== segmentoFiltro) {
        return false;
      }
      if (riesgoFiltro !== "TODOS" && c.stats.riesgoChurn !== riesgoFiltro) {
        return false;
      }
      return true;
    });
  }, [clientes, segmentoFiltro, riesgoFiltro]);

  const interaccionesByCliente = useMemo(() => {
    const map = {};
    (store.interaccionesWhatsapp || []).forEach((i) => {
      if (!map[i.cliente_id]) map[i.cliente_id] = [];
      map[i.cliente_id].push(i);
    });
    return map;
  }, [store.interaccionesWhatsapp]);

  const interesesPorCliente = useMemo(() => {
    const map = {};
    (store.interesesCatalogo || []).forEach((i) => {
      if (!i.cliente_id) return;
      if (!map[i.cliente_id]) map[i.cliente_id] = [];
      map[i.cliente_id].push(i);
    });
    return map;
  }, [store.interesesCatalogo]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGuardarCliente = async () => {
    if (!form.nombre.trim()) {
      setFeedback({ tipo: "error", msg: "Nombre es obligatorio" });
      return;
    }
    const waNuevo = normalizeFullPhone(form.whatsapp);
    if (!isValidFullPhone(waNuevo)) {
      setFeedback({
        tipo: "error",
        msg: "WhatsApp inválido: selecciona país (+591) y al menos 7 dígitos",
      });
      return;
    }

    const clienteDuplicado = (store.clientes || []).find(
      (c) => normPhoneDigits(c.whatsapp) === waNuevo
    );
    if (clienteDuplicado) {
      setFeedback({
        tipo: "warning",
        msg: `Ya existe un cliente con ese WhatsApp: ${clienteDuplicado.nombre}`,
      });
      return;
    }

    let clienteId = null;
    try {
      clienteId = await store.upsertCliente({
        nombre: form.nombre,
        whatsapp: waNuevo,
        ciudad: form.ciudad,
        zona: form.zona,
        estilo_preferido: form.estilo_preferido,
        sensibilidad_precio: form.sensibilidad_precio,
        tallas: {
          tops: form.talla_tops,
          jeans: form.talla_jeans,
          vestidos: form.talla_vestidos,
          calzado: form.talla_calzado,
        },
        colores_preferidos: form.colores_preferidos
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean),
        consentimiento_whatsapp: true,
      });
    } catch {
      setFeedback({ tipo: "error", msg: "No se pudo guardar el cliente" });
      return;
    }

    if (!clienteId) {
      setFeedback({ tipo: "error", msg: "No se pudo guardar el cliente" });
      return;
    }

    setFeedback({ tipo: "success", msg: "Cliente guardado en CRM" });
    setForm(initialForm);
  };

  const handleFlow = async (cliente, flow) => {
    let result;
    try {
      result = await store.enviarCampanaWhatsapp({
        cliente_id: cliente.id,
        tipo: flow.key,
        plantilla: flow.plantilla,
        metadata: {
          cliente_nombre: cliente.nombre,
          segmento: cliente.stats.segmento,
          riesgo: cliente.stats.riesgoChurn,
          estilo: cliente.estilo_preferido,
          categorias: cliente.stats.categoriasTop,
        },
      });
    } catch {
      const msg = `No se pudo enviar a ${cliente.nombre}: error de red o servidor`;
      setFeedback({ tipo: "error", msg });
      notifyWhatsappFlow("error", msg, {
        clienteId: cliente.id,
        flowLabel: flow.label,
      });
      return;
    }

    if (result?.ok) {
      if (result.wa_url) {
        window.open(result.wa_url, "_blank", "noopener,noreferrer");
      }
      const msg = `Campaña «${flow.label}» lista para ${cliente.nombre}`;
      setFeedback({ tipo: "success", msg });
      notifyWhatsappFlow("success", msg, { clienteId: cliente.id, flowLabel: flow.label });
      return;
    }

    const reason = String(result?.reason || "No disponible");
    const msg = `${cliente.nombre}: ${reason}`;
    setFeedback({ tipo: "warning", msg: `WhatsApp — ${msg}` });
    notifyWhatsappFlow("warning", msg, {
      clienteId: cliente.id,
      flowLabel: flow.label,
    });
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={2}>
        CRM Moda + WhatsApp
      </Typography>

      {feedback && (
        <Alert severity={feedback.tipo} sx={{ mb: 2 }}>
          {feedback.msg}
        </Alert>
      )}

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary">Clientes</Typography>
              <Typography variant="h4" fontWeight={800}>
                {resumen.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary">VIP</Typography>
              <Typography variant="h4" color="warning.main" fontWeight={800}>
                {resumen.vip}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary">Riesgo Alto</Typography>
              <Typography variant="h4" color="error.main" fontWeight={800}>
                {resumen.riesgoAlto}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary">Con WhatsApp</Typography>
              <Typography variant="h4" color="success.main" fontWeight={800}>
                {resumen.activosWA}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <CatalogInteresesPanel store={store} clientes={clientes} />

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <PersonAddAlt1Icon color="primary" />
            <Typography fontWeight={700}>Alta rapida de cliente</Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Nombre"
                value={form.nombre}
                onChange={(e) => setField("nombre", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <PhoneWithCountryField
                label="WhatsApp"
                required
                value={form.whatsapp}
                onChange={(v) => setField("whatsapp", v)}
                helperText="Código de país obligatorio (por defecto Bolivia +591)."
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Ciudad"
                value={form.ciudad}
                onChange={(e) => setField("ciudad", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Zona"
                value={form.zona}
                onChange={(e) => setField("zona", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <Select
                value={form.estilo_preferido}
                onChange={(e) => setField("estilo_preferido", e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="CASUAL">Casual</MenuItem>
                <MenuItem value="URBANO">Urbano</MenuItem>
                <MenuItem value="FORMAL">Formal</MenuItem>
                <MenuItem value="DEPORTIVO">Deportivo</MenuItem>
                <MenuItem value="MINIMALISTA">Minimalista</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6} md={2}>
              <Select
                value={form.sensibilidad_precio}
                onChange={(e) => setField("sensibilidad_precio", e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="ALTA">Precio Alta</MenuItem>
                <MenuItem value="MEDIA">Precio Media</MenuItem>
                <MenuItem value="BAJA">Precio Baja</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Talla tops"
                value={form.talla_tops}
                onChange={(e) => setField("talla_tops", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Talla jeans"
                value={form.talla_jeans}
                onChange={(e) => setField("talla_jeans", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Talla vestidos"
                value={form.talla_vestidos}
                onChange={(e) => setField("talla_vestidos", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Talla calzado"
                value={form.talla_calzado}
                onChange={(e) => setField("talla_calzado", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Colores preferidos (coma)"
                value={form.colores_preferidos}
                onChange={(e) => setField("colores_preferidos", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleGuardarCliente}
                sx={{ height: "100%" }}
              >
                Guardar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
        <Select
          value={segmentoFiltro}
          onChange={(e) => setSegmentoFiltro(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="TODOS">Todos los segmentos</MenuItem>
          <MenuItem value="VIP">VIP</MenuItem>
          <MenuItem value="FRECUENTE">Frecuente</MenuItem>
          <MenuItem value="OCASIONAL">Ocasional</MenuItem>
          <MenuItem value="NUEVO">Nuevo</MenuItem>
        </Select>

        <Select
          value={riesgoFiltro}
          onChange={(e) => setRiesgoFiltro(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="TODOS">Todos los riesgos</MenuItem>
          <MenuItem value="ALTO">Riesgo Alto</MenuItem>
          <MenuItem value="MEDIO">Riesgo Medio</MenuItem>
          <MenuItem value="BAJO">Riesgo Bajo</MenuItem>
        </Select>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700 } }}>
              <TableCell>Cliente</TableCell>
              <TableCell>Perfil</TableCell>
              <TableCell align="center">Segmento</TableCell>
              <TableCell align="center">Riesgo</TableCell>
              <TableCell align="right">LTV</TableCell>
              <TableCell align="center">Frecuencia</TableCell>
              <TableCell align="center">Intereses</TableCell>
              <TableCell align="center">WhatsApp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientesFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No hay clientes para este filtro</Typography>
                </TableCell>
              </TableRow>
            ) : (
              clientesFiltrados.map((cliente) => {
                const stats = cliente.stats;
                const historial = interaccionesByCliente[cliente.id] || [];
                const interesesCli = interesesPorCliente[cliente.id] || [];
                const lastMsg = historial.length
                  ? historial[historial.length - 1].fecha
                  : null;
                return (
                  <TableRow key={cliente.id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>{cliente.nombre}</Typography>
                      {(() => {
                        const waDisplay =
                          formatPhoneDisplay(cliente.whatsapp) || cliente.whatsapp;
                        const waLink = whatsappHref(cliente.whatsapp);
                        return waLink ? (
                          <Link
                            href={waLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.35 }}
                          >
                            <WhatsAppIcon sx={{ fontSize: 14, color: "#25D366" }} />
                            {waDisplay}
                          </Link>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {waDisplay || "—"}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {cliente.estilo_preferido || "SIN ESTILO"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tops {cliente?.tallas?.tops || "-"} | Jeans {cliente?.tallas?.jeans || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={stats.segmento}
                        size="small"
                        color={segmentoColor[stats.segmento] || "default"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={stats.riesgoChurn}
                        size="small"
                        color={riesgoColor[stats.riesgoChurn] || "default"}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {store.config.moneda} {stats.montoAcumulado.toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      {stats.frecuenciaDias ? `${stats.frecuenciaDias.toFixed(0)}d` : "-"}
                    </TableCell>
                    <TableCell align="center">
                      {interesesCli.length > 0 ? (
                        <Chip
                          size="small"
                          icon={<StarIcon sx={{ fontSize: 14 }} />}
                          label={interesesCli.length}
                          sx={{ bgcolor: "#fff7ed", color: "#b45309", fontWeight: 700 }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        useFlexGap
                        flexWrap="wrap"
                        justifyContent="center"
                      >
                        {whatsappFlows.map((flow) => (
                          <Button
                            key={`${cliente.id}-${flow.key}`}
                            size="small"
                            variant="outlined"
                            startIcon={<WhatsAppIcon />}
                            onClick={() => handleFlow(cliente, flow)}
                          >
                            {flow.label}
                          </Button>
                        ))}
                      </Stack>
                      <Divider sx={{ my: 0.75 }} />
                      <Typography variant="caption" color="text.secondary">
                        {lastMsg
                          ? `Ultimo mensaje: ${new Date(lastMsg).toLocaleDateString("es")}`
                          : "Sin mensajes"}
                      </Typography>
                      {waInline?.clienteId === cliente.id && (
                        <Box
                          role="alert"
                          sx={{
                            mt: 1,
                            px: 1.25,
                            py: 0.85,
                            borderRadius: 1.5,
                            bgcolor: "warning.50",
                            border: "1px solid",
                            borderColor: "warning.light",
                            textAlign: "left",
                          }}
                        >
                          <Typography
                            variant="caption"
                            component="div"
                            fontWeight={700}
                            color="warning.dark"
                            sx={{ lineHeight: 1.35 }}
                          >
                            {waInline.flowLabel ? `${waInline.flowLabel} — ` : ""}
                            {waInline.msg}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={Boolean(waSnack)}
        autoHideDuration={7000}
        onClose={() => setWaSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setWaSnack(null)}
          severity={waSnack?.severity || "warning"}
          variant="filled"
          elevation={6}
          sx={{
            width: "100%",
            maxWidth: 440,
            fontSize: "0.875rem",
            alignItems: "center",
            "& .MuiAlert-message": { py: 0.25 },
          }}
        >
          {waSnack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
